import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Alarm as AlarmIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Snooze as SnoozeIcon,
  Close as DismissIcon,
  Task as TaskIcon,
  Folder as ProjectIcon,
  Person as ClientIcon,
  Repeat as RecurringIcon,
} from '@mui/icons-material';
import { format, isPast, isToday, isTomorrow } from 'date-fns';
import { useReminders, useReminderMutations } from '../../hooks/useReminders';
import { useTasks } from '../../hooks/useTasks';
import { useProjects } from '../../hooks/useProjects';
import { useClients } from '../../hooks/useClients';
import { ReminderForm } from '../../components/notifications';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import type {
  ReminderWithRelations,
  ReminderFilters,
  CreateReminderRequest,
  UpdateReminderRequest,
  ReminderStatus,
} from '../../types/reminder';
import { SNOOZE_OPTIONS } from '../../types/reminder';

export const RemindersPage: React.FC = () => {
  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState<ReminderWithRelations | null>(null);
  const [reminderToDelete, setReminderToDelete] = useState<ReminderWithRelations | null>(null);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuReminder, setMenuReminder] = useState<ReminderWithRelations | null>(null);

  // Snooze menu
  const [snoozeAnchor, setSnoozeAnchor] = useState<null | HTMLElement>(null);
  const [snoozeReminder, setSnoozeReminder] = useState<ReminderWithRelations | null>(null);

  // Form data
  const [formData, setFormData] = useState<CreateReminderRequest | UpdateReminderRequest>({});

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Filters based on tab
  const getFilters = (): ReminderFilters => {
    switch (tabValue) {
      case 0: // Upcoming
        return { status: ['pending', 'snoozed'], upcoming: true, sortBy: 'remindAt', sortOrder: 'asc' };
      case 1: // All Active
        return { status: ['pending', 'triggered', 'snoozed'], sortBy: 'remindAt', sortOrder: 'asc' };
      case 2: // Dismissed
        return { status: ['dismissed'], sortBy: 'remindAt', sortOrder: 'desc' };
      default:
        return {};
    }
  };

  // Data fetching
  const { data, isLoading } = useReminders(getFilters());
  const mutations = useReminderMutations();
  const { data: tasksData } = useTasks({ status: ['todo', 'in_progress'] });
  const { data: projectsData } = useProjects({ status: ['planning', 'active'] });
  const { data: clientsData } = useClients();

  const reminders = data?.data?.reminders || [];
  const tasks = tasksData?.data?.tasks || [];
  const projects = projectsData?.data?.projects || [];
  const clients = clientsData?.clients || [];

  // Handlers
  const handleOpenNew = () => {
    setSelectedReminder(null);
    setFormData({});
    setIsFormOpen(true);
  };

  const handleEdit = (reminder: ReminderWithRelations) => {
    setSelectedReminder(reminder);
    setIsFormOpen(true);
    setMenuAnchor(null);
  };

  const handleSave = async () => {
    if (!formData.title?.trim() || !formData.remindAt) return;

    try {
      if (selectedReminder) {
        await mutations.updateReminder.mutateAsync({
          id: selectedReminder._id,
          data: formData as UpdateReminderRequest,
        });
        setSnackbar({ open: true, message: 'Reminder updated', severity: 'success' });
      } else {
        await mutations.createReminder.mutateAsync(formData as CreateReminderRequest);
        setSnackbar({ open: true, message: 'Reminder created', severity: 'success' });
      }
      setIsFormOpen(false);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to save',
        severity: 'error',
      });
    }
  };

  const handleSnooze = async (minutes: number) => {
    if (!snoozeReminder) return;

    try {
      await mutations.snoozeReminder.mutateAsync({
        id: snoozeReminder._id,
        minutes,
      });
      setSnackbar({ open: true, message: `Snoozed for ${minutes} minutes`, severity: 'success' });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to snooze',
        severity: 'error',
      });
    }
    setSnoozeAnchor(null);
    setSnoozeReminder(null);
  };

  const handleDismiss = async (reminder: ReminderWithRelations) => {
    try {
      await mutations.dismissReminder.mutateAsync(reminder._id);
      setSnackbar({ open: true, message: 'Reminder dismissed', severity: 'success' });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to dismiss',
        severity: 'error',
      });
    }
    setMenuAnchor(null);
  };

  const handleDelete = async () => {
    if (!reminderToDelete) return;

    try {
      await mutations.deleteReminder.mutateAsync(reminderToDelete._id);
      setSnackbar({ open: true, message: 'Reminder deleted', severity: 'success' });
      setReminderToDelete(null);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to delete',
        severity: 'error',
      });
    }
  };

  const formatReminderTime = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) return `Today at ${format(date, 'h:mm a')}`;
    if (isTomorrow(date)) return `Tomorrow at ${format(date, 'h:mm a')}`;
    return format(date, 'MMM d, yyyy h:mm a');
  };

  const getReminderTimeColor = (dateStr: string, status: ReminderStatus) => {
    if (status === 'dismissed') return 'text.secondary';
    const date = new Date(dateStr);
    if (isPast(date)) return 'error.main';
    if (isToday(date)) return 'warning.main';
    return 'text.primary';
  };

  // Empty state
  const EmptyState = () => (
    <Paper sx={{ textAlign: 'center', py: 8, px: 4 }}>
      <AlarmIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" gutterBottom>
        No reminders
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Create a reminder to never miss important tasks
      </Typography>
      <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenNew}>
        Create Reminder
      </Button>
    </Paper>
  );

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Reminders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {reminders.length} reminders
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenNew}>
          New Reminder
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Upcoming" />
          <Tab label="All Active" />
          <Tab label="Dismissed" />
        </Tabs>
      </Paper>

      {/* Reminders List */}
      {isLoading ? (
        <Typography>Loading...</Typography>
      ) : reminders.length === 0 ? (
        <EmptyState />
      ) : (
        <Stack spacing={2}>
          {reminders.map((reminder) => (
            <Paper key={reminder._id} sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack direction="row" spacing={2} alignItems="flex-start" flex={1}>
                  {/* Icon */}
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AlarmIcon color="primary" />
                  </Box>

                  {/* Content */}
                  <Box flex={1}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {reminder.title}
                      </Typography>
                      {reminder.reminderType === 'recurring' && (
                        <Tooltip title="Recurring">
                          <RecurringIcon fontSize="small" color="action" />
                        </Tooltip>
                      )}
                      {reminder.status === 'snoozed' && (
                        <Chip label="Snoozed" size="small" color="secondary" />
                      )}
                    </Stack>

                    <Typography
                      variant="body2"
                      sx={{ color: getReminderTimeColor(reminder.remindAt, reminder.status) }}
                    >
                      {formatReminderTime(reminder.remindAt)}
                    </Typography>

                    {reminder.description && (
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {reminder.description}
                      </Typography>
                    )}

                    {/* Links */}
                    <Stack direction="row" spacing={1} mt={1}>
                      {reminder.taskId && (
                        <Chip
                          icon={<TaskIcon />}
                          label={reminder.taskId.title}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {reminder.projectId && (
                        <Chip
                          icon={<ProjectIcon />}
                          label={reminder.projectId.name}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {reminder.clientId && (
                        <Chip
                          icon={<ClientIcon />}
                          label={`${reminder.clientId.firstName} ${reminder.clientId.lastName}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Box>
                </Stack>

                {/* Actions */}
                <Stack direction="row" spacing={0.5}>
                  {reminder.status !== 'dismissed' && (
                    <Tooltip title="Snooze">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setSnoozeAnchor(e.currentTarget);
                          setSnoozeReminder(reminder);
                        }}
                      >
                        <SnoozeIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      setMenuAnchor(e.currentTarget);
                      setMenuReminder(reminder);
                    }}
                  >
                    <MoreIcon />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={() => menuReminder && handleEdit(menuReminder)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        {menuReminder?.status !== 'dismissed' && (
          <MenuItem onClick={() => menuReminder && handleDismiss(menuReminder)}>
            <ListItemIcon><DismissIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Dismiss</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setReminderToDelete(menuReminder);
            setMenuAnchor(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Snooze Menu */}
      <Menu
        anchorEl={snoozeAnchor}
        open={Boolean(snoozeAnchor)}
        onClose={() => setSnoozeAnchor(null)}
      >
        {SNOOZE_OPTIONS.map((option) => (
          <MenuItem key={option.value} onClick={() => handleSnooze(option.value)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedReminder ? 'Edit Reminder' : 'New Reminder'}
        </DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <ReminderForm
              reminder={selectedReminder}
              onChange={setFormData}
              tasks={tasks}
              projects={projects}
              clients={clients}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFormOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.title?.trim() || !formData.remindAt || mutations.isLoading}
          >
            {mutations.isLoading ? 'Saving...' : selectedReminder ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!reminderToDelete}
        title="Delete Reminder"
        message={`Delete "${reminderToDelete?.title}"?`}
        confirmText="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setReminderToDelete(null)}
        loading={mutations.deleteReminder.isPending}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RemindersPage;
